import { Box, Group, Stack, Image, useMantineTheme } from "@mantine/core"
import { MIME_TYPES } from "@mantine/dropzone"
import { IconTrash, IconUpload } from "@tabler/icons-react"
import {
  Button,
  DocumentUploader,
  EqualWidthGrid,
  FileInput,
  InfoBox,
} from "@tfg/ui"
import { useRef } from "react"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { FormFileType } from "@tfg/types/schemas/file"
import { ContentFilteringProps } from "@tfg/types/types/communication-studio/configurations.types"

import { queryClient } from "~/backend/queryClient"
import {
  getDesignTokensQueryKey,
  getProductPageComponentsQueryKey,
  useDeleteLogo,
} from "~/gen/api"
import { useUploadLogo } from "~/pages/Brand/CommunicationStudio/DesignStudio/hooks/useUploadLogo"
import { useProductSheetGroup } from "~/pages/Brand/CommunicationStudio/contexts/productSheetGroup/useProductSheetGroup"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export interface LogoRendererProps {
  logoFile: FormFileType | null
  updateModalConfigurations?: (
    configurations: Partial<ContentFilteringProps> | null
  ) => void
  validationErrors?: z.ZodError
  description?: string
}

/**
 * Renders logo configuration editor
 * Allows users to upload and configure the logo file
 */
export const LogoRenderer = ({
  logoFile,
  updateModalConfigurations,
  validationErrors,
  description,
}: LogoRendererProps) => {
  const { t } = useTranslation("communicationStudio")
  const theme = useMantineTheme()
  const { selectedProductSheetGroup } = useProductSheetGroup()
  const { mutateAsync: uploadLogo } = useUploadLogo({
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: getDesignTokensQueryKey(selectedProductSheetGroup?.id ?? ""),
      })
      queryClient.invalidateQueries({
        queryKey: getProductPageComponentsQueryKey(
          selectedProductSheetGroup?.id ?? ""
        ),
      })
    },
    onError: (error) => {
      showAxiosError({
        error,
        notificationTitle: t("logoUpload.error.title"),
        notificationMessage: t("logoUpload.error.message"),
        showNotification: true,
      })
    },
  })

  const { mutateAsync: deleteLogo } = useDeleteLogo({
    mutation: {
      onSuccess: () => {
        showSuccessNotification({
          title: t("logoDelete.success.title"),
          message: t("logoDelete.success.message"),
        })
        queryClient.invalidateQueries({
          queryKey: getDesignTokensQueryKey(
            selectedProductSheetGroup?.id ?? ""
          ),
        })
        queryClient.invalidateQueries({
          queryKey: getProductPageComponentsQueryKey(
            selectedProductSheetGroup?.id ?? ""
          ),
        })
      },
      onError: (error) => {
        showAxiosError({
          error,
          notificationTitle: t("logoDelete.error.title"),
          notificationMessage: t("logoDelete.error.message"),
          showNotification: true,
        })
      },
    },
  })

  const fileInputRef = useRef<HTMLButtonElement>(null)

  const handleFileChange = async (file: File | null) => {
    if (file && updateModalConfigurations) {
      const path = await uploadLogo(file)
      updateModalConfigurations({
        logoFile: {
          name: file.name,
          url: URL.createObjectURL(file),
          path,
        },
      })

      await queryClient.invalidateQueries({
        queryKey: getDesignTokensQueryKey(selectedProductSheetGroup?.id ?? ""),
      })
    }
  }

  const handleRemove = async () => {
    if (logoFile?.path) {
      await deleteLogo({ data: { logoPath: logoFile.path } })
    }
    if (updateModalConfigurations) {
      updateModalConfigurations({ logoFile: null })
    }

    await queryClient.invalidateQueries({
      queryKey: getDesignTokensQueryKey(selectedProductSheetGroup?.id ?? ""),
    })
    await queryClient.invalidateQueries({
      queryKey: getProductPageComponentsQueryKey(
        selectedProductSheetGroup?.id ?? ""
      ),
    })
  }

  const handleChangeClick = () => {
    fileInputRef.current?.click()
  }

  if (validationErrors && process.env.NODE_ENV === "local") {
    console.warn("LogoRenderer validationErrors", validationErrors)
  }

  return (
    <Stack gap="md">
      {description && (
        <InfoBox
          title={t("tabs.content.componentModal.descriptions.LOGO.title")}
          message={description}
        />
      )}
      {logoFile ? (
        <Box
          style={{
            border: "1px solid var(--mantine-color-gray-2)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <Box
            bg="gray.1"
            bdrs="var(--mantine-radius-md)"
            style={{ overflow: "hidden" }}
          >
            {/* Logo Preview */}
            <Group align="center" justify="center" mih="100px">
              <Image
                src={logoFile.url}
                alt="Logo preview"
                width="100%"
                mah="50px"
                fit="contain"
                height="100%"
              />
            </Group>

            {/* Action Buttons */}
            <EqualWidthGrid
              p="xs"
              bg="white"
              bdrs="0px 0px var(--mantine-radius-md) var(--mantine-radius-md)"
            >
              <Button
                variant="secondary"
                fullWidth
                onClick={handleChangeClick}
                icon={<IconUpload size={theme.other.icon.size} />}
              >
                {t("tabs.content.logoModal.changeLogo")}
              </Button>
              <Button
                variant="secondary-critical"
                fullWidth
                icon={<IconTrash size={theme.other.icon.size} />}
                onClick={handleRemove}
              >
                {t("tabs.content.logoModal.removeLogo")}
              </Button>
            </EqualWidthGrid>
          </Box>

          {/* Hidden file input for changing logo */}
          <div style={{ display: "none" }}>
            <FileInput
              ref={fileInputRef}
              label=""
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>
        </Box>
      ) : (
        <Box>
          <DocumentUploader
            title={t("tabs.content.componentModal.descriptions.LOGO.title")}
            helpText={t(
              "tabs.content.componentModal.descriptions.LOGO.helpText"
            )}
            accept={[
              MIME_TYPES.png,
              MIME_TYPES.jpeg,
              MIME_TYPES.svg,
              MIME_TYPES.webp,
            ]}
            multiple={false}
            maxSize={5 * 1024 * 1024} // 5MB max
            onDrop={(files) => handleFileChange(files[0])}
          />
        </Box>
      )}
    </Stack>
  )
}
